"use client"

import { useMemo, useState } from "react"

import { CalculatorCard } from "@/components/calculators/calculator-card"
import { CalculatorResult } from "@/components/calculators/calculator-result"
import { KuhlykTip } from "@/components/kuhlyk/kyhlyk-tip"

type Strength = 3 | 6 | 9

const MIN_AMOUNT_ML = 1
const MAX_AMOUNT_ML = 5000

function roundToOne(value: number) {
  return Math.round(value * 10) / 10
}

function sanitizeNumber(
  value: number,
  min: number,
  max: number,
  fallback: number
) {
  if (!Number.isFinite(value)) {
    return fallback
  }

  return Math.min(max, Math.max(min, value))
}

export function VinegarConverter() {
  const [fromStrength, setFromStrength] = useState<Strength>(9)
  const [toStrength, setToStrength] = useState<Strength>(6)
  const [amountMl, setAmountMl] = useState<number>(100)

  const safeAmountMl = sanitizeNumber(amountMl, MIN_AMOUNT_ML, MAX_AMOUNT_ML, 100)

  const result = useMemo(() => {
    const converted = (safeAmountMl * fromStrength) / toStrength
    return roundToOne(converted)
  }, [safeAmountMl, fromStrength, toStrength])

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
      <CalculatorCard
        title="Конвертер оцту"
        description="Перерахунок між 3%, 6% і 9% оцтом. Корисно, коли в рецепті вказана одна концентрація, а у вас під рукою інша."
      >
        <div className="grid gap-5">
          <label className="block" htmlFor="amount-ml">
            <span className="mb-2 block text-sm font-medium text-neutral-800">
              Кількість (мл)
            </span>

            <input
              id="amount-ml"
              type="number"
              min={MIN_AMOUNT_ML}
              max={MAX_AMOUNT_ML}
              step={1}
              inputMode="numeric"
              value={amountMl}
              onChange={(e) => setAmountMl(Number(e.target.value))}
              className="w-full rounded-2xl border border-neutral-200 bg-white px-4 py-3 text-sm text-neutral-900 outline-none transition focus:border-emerald-400"
            />
          </label>

          <label className="block" htmlFor="from-strength">
            <span className="mb-2 block text-sm font-medium text-neutral-800">
              Є в наявності
            </span>

            <select
              id="from-strength"
              value={fromStrength}
              onChange={(e) =>
                setFromStrength(Number(e.target.value) as Strength)
              }
              className="w-full rounded-2xl border border-neutral-200 bg-white px-4 py-3 text-sm text-neutral-900 outline-none transition focus:border-emerald-400"
            >
              <option value={3}>Оцет 3%</option>
              <option value={6}>Оцет 6%</option>
              <option value={9}>Оцет 9%</option>
            </select>
          </label>

          <label className="block" htmlFor="to-strength">
            <span className="mb-2 block text-sm font-medium text-neutral-800">
              Потрібно отримати еквівалент
            </span>

            <select
              id="to-strength"
              value={toStrength}
              onChange={(e) => setToStrength(Number(e.target.value) as Strength)}
              className="w-full rounded-2xl border border-neutral-200 bg-white px-4 py-3 text-sm text-neutral-900 outline-none transition focus:border-emerald-400"
            >
              <option value={3}>Оцет 3%</option>
              <option value={6}>Оцет 6%</option>
              <option value={9}>Оцет 9%</option>
            </select>
          </label>
        </div>
      </CalculatorCard>

      <div className="space-y-5">
        <CalculatorResult
          title="Результат перерахунку"
          items={[
            {
              label: "Еквівалентна кількість",
              value: `${result} мл`,
              hint: `${safeAmountMl} мл ${fromStrength}% ≈ ${result} мл ${toStrength}%`,
            },
          ]}
          note="Формула розрахунку базується на співвідношенні концентрацій. Це корисний орієнтир для заміни одного виду оцту іншим у межах однакової кислотної еквівалентності."
        />

        <KuhlykTip
          variant="warning"
          title="Важливе зауваження"
          text="Якщо в рецепті кислотність критична для безпечного зберігання, краще дотримуватися вихідної рецептури й не робити довільних замін без перевірки."
        />
      </div>
    </div>
  )
}